import { TableV2 as Table } from "@asayinc/component-library";
import { FilterList } from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { VOTING_PROXY_EVENT_TYPE_OPTIONS } from "src/constants/options/proxyEventOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useProxyEventsQuery } from "src/store/proxyEvent";
import { getColumnData, getColumns, getRowData } from "./utils";
import { IProxyEvent } from "src/store/proxyEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export function IssuerProxyEvents() {
  const { id: issuerId } = useParams() as { id: string };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const methods = useForm();
  const view = searchParams.get(TABLE_SEARCH_PARAMS.VIEW);
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useProxyEventsQuery({
    offset,
    limit,
    ordering,
    types: type ? [type] : undefined,
    issuerId,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IProxyEvent) => {
    const { id } = event;
    navigate(`/proxy-events/${id}/details`);
  };

  const columns = useMemo(() => getColumns(), []);
  const columnData = useMemo(() => {
    return getColumnData({
      getSort,
      onOrderingChange,
    });
  }, [getSort, onOrderingChange]);
  const rowData = getRowData(data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={VOTING_PROXY_EVENT_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view || undefined}
      />
    </FormProvider>
  );

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Table
        count={data?.count || 0}
        columnData={columnData}
        columns={columns}
        isFetching={isFetching}
        isLoading={isLoading}
        noResultsData={{
          title: "No Proxy Events found for this issuer",
          subtitle:
            "Sorry, there are no Proxy Events that match your applied filters.",
        }}
        paginateProps={paginateProps}
        rows={rowData}
        tableMinWidth={1100}
        title="Proxy Events"
        titleVariant="subtitle2"
        toolbarInside
        toolbarCollapse={{
          Icon: FilterList,
          collapseContent: filterList,
          isFlat: true,
        }}
      />
    </ScrollContainer>
  );
}
